/**
 * §12.4 — Voice primitives. Every sound in the game is built from these; nothing is
 * fetched. Each primitive returns a `Voice` whose `gain` node the mixer can fade for a
 * 20 ms steal (§12.2 rule 5) and whose sources it can stop.
 *
 * Envelope notation follows §12.4: A/D/S/R in milliseconds, gains linear.
 */

import type { AudioGraph } from './context';

/** Floor for exponential ramps — WebAudio rejects a target of exactly zero. */
export const EPS = 0.0001;

export interface Voice {
  /** Envelope / level node. Disconnecting this silences the whole voice. */
  gain: GainNode;
  /** Context time the voice is finished at; Infinity for loops. */
  endAt: number;
  sources: AudioScheduledSourceNode[];
  /** Optional filter cutoff driven by music intensity I (§12.4 R-DRONE, R-MUSIC-L2). */
  tracking: AudioParam | null;
  trackLo: number;
  trackHi: number;
}

/** Preallocated empty source list template is not reusable (nodes differ per voice). */
export function makeVoice(gain: GainNode, endAt: number): Voice {
  return { gain, endAt, sources: [], tracking: null, trackLo: 0, trackHi: 0 };
}

export function midiToFreq(midi: number): number {
  return 440 * Math.pow(2, (midi - 69) / 12);
}

/** ±cents as an exponential ratio, so + and − sound symmetric. */
export function centsRatio(cents: number): number {
  return Math.pow(2, cents / 1200);
}

/**
 * Attack/decay one-shot envelope. `sustain` 0 means the voice ends at A+D.
 * Returns the context time the envelope reaches silence.
 */
export function envAD(
  param: AudioParam,
  t0: number,
  peak: number,
  attackMs: number,
  decayMs: number,
  sustain = 0,
  holdS = 0,
  releaseMs = 0,
): number {
  const a = Math.max(0.0005, attackMs / 1000);
  const d = Math.max(0.001, decayMs / 1000);
  param.cancelScheduledValues(t0);
  param.setValueAtTime(EPS, t0);
  param.linearRampToValueAtTime(Math.max(EPS, peak), t0 + a);
  if (sustain > 0) {
    param.exponentialRampToValueAtTime(Math.max(EPS, peak * sustain), t0 + a + d);
    const r = Math.max(0.001, releaseMs / 1000);
    param.setValueAtTime(Math.max(EPS, peak * sustain), t0 + a + d + holdS);
    param.exponentialRampToValueAtTime(EPS, t0 + a + d + holdS + r);
    return t0 + a + d + holdS + r;
  }
  param.exponentialRampToValueAtTime(EPS, t0 + a + d);
  return t0 + a + d;
}

function osc(g: AudioGraph, type: OscillatorType, freq: number): OscillatorNode {
  const o = g.ctx.createOscillator();
  o.type = type;
  o.frequency.value = freq;
  return o;
}

function noiseSource(g: AudioGraph, brown: boolean, t0: number, dur: number): AudioBufferSourceNode {
  const s = g.ctx.createBufferSource();
  s.buffer = brown ? g.brown : g.noise;
  s.loop = true;
  // Deterministic read offset per voice start so successive bursts are not identical.
  const span = (s.buffer ? s.buffer.duration : 2) - 0.001;
  s.loopStart = 0;
  s.loopEnd = span;
  s.start(t0, ((t0 * 7.13) % span + span) % span);
  s.stop(t0 + dur);
  return s;
}

// ---------------------------------------------------------------------------
// Tonal primitives
// ---------------------------------------------------------------------------

/**
 * Lead: two saws detuned ±8 cents plus a sub square, through a resonant lowpass with an
 * ADSR on a GainNode. Ported from the reference synth voice.
 */
export function leadVoice(
  g: AudioGraph,
  dest: AudioNode,
  t0: number,
  t1: number,
  freq: number,
  velocity: number,
  peakCap = 0.2,
): Voice {
  const peak = Math.min(peakCap, (velocity / 127) * 0.22);
  const filter = g.ctx.createBiquadFilter();
  filter.type = 'lowpass';
  filter.frequency.setValueAtTime(Math.min(5200, freq * 6 + 600), t0);
  filter.frequency.exponentialRampToValueAtTime(Math.max(400, freq * 2.2), Math.max(t0 + 0.02, t1));
  filter.Q.value = 6;
  const gain = g.ctx.createGain();
  gain.gain.setValueAtTime(EPS, t0);
  gain.gain.linearRampToValueAtTime(peak, t0 + 0.008);
  gain.gain.linearRampToValueAtTime(peak * 0.55, t0 + 0.06);
  gain.gain.setTargetAtTime(EPS, Math.max(t0 + 0.07, t1 - 0.12), 0.05);
  filter.connect(gain);
  gain.connect(dest);

  const v = makeVoice(gain, t1 + 0.15);
  const stopAt = t1 + 0.1;
  const o1 = osc(g, 'sawtooth', freq);
  o1.detune.value = -8;
  o1.connect(filter);
  o1.start(t0);
  o1.stop(stopAt);
  const o2 = osc(g, 'sawtooth', freq);
  o2.detune.value = 8;
  o2.connect(filter);
  o2.start(t0);
  o2.stop(stopAt);
  const sub = osc(g, 'square', freq * 0.5);
  const subGain = g.ctx.createGain();
  subGain.gain.value = 0.35;
  sub.connect(subGain);
  subGain.connect(filter);
  sub.start(t0);
  sub.stop(stopAt);
  v.sources.push(o1, o2, sub);
  v.tracking = filter.frequency;
  v.trackLo = Math.max(400, freq * 2.2);
  v.trackHi = Math.min(5200, freq * 6 + 600);
  return v;
}

/** §12.4 R-MUSIC-L1 pad: 2× triangle + 1 sine drone through a lowpass at 800 Hz. */
export function padVoice(g: AudioGraph, dest: AudioNode, t0: number, t1: number, freq: number, level: number): Voice {
  const filter = g.ctx.createBiquadFilter();
  filter.type = 'lowpass';
  filter.frequency.value = 800;
  filter.Q.value = 0.7;
  const gain = g.ctx.createGain();
  const attack = Math.min(0.6, (t1 - t0) * 0.25);
  const release = Math.min(0.9, (t1 - t0) * 0.4);
  gain.gain.setValueAtTime(EPS, t0);
  gain.gain.linearRampToValueAtTime(level, t0 + attack);
  gain.gain.setValueAtTime(level, Math.max(t0 + attack, t1 - release));
  gain.gain.exponentialRampToValueAtTime(EPS, t1 + 0.05);
  filter.connect(gain);
  gain.connect(dest);

  const v = makeVoice(gain, t1 + 0.1);
  const stopAt = t1 + 0.08;
  const a = osc(g, 'triangle', freq);
  a.detune.value = -5;
  const b = osc(g, 'triangle', freq);
  b.detune.value = 5;
  const c = osc(g, 'sine', freq * 0.5);
  const cg = g.ctx.createGain();
  cg.gain.value = 0.5;
  a.connect(filter);
  b.connect(filter);
  c.connect(cg);
  cg.connect(filter);
  a.start(t0);
  b.start(t0);
  c.start(t0);
  a.stop(stopAt);
  b.stop(stopAt);
  c.stop(stopAt);
  v.sources.push(a, b, c);
  return v;
}

/** Sub bass: square an octave-and-a-half down through a gentle lowpass. */
export function subVoice(g: AudioGraph, dest: AudioNode, t0: number, t1: number, freq: number, level: number): Voice {
  const filter = g.ctx.createBiquadFilter();
  filter.type = 'lowpass';
  filter.frequency.value = 320;
  const gain = g.ctx.createGain();
  gain.gain.setValueAtTime(EPS, t0);
  gain.gain.linearRampToValueAtTime(level, t0 + 0.012);
  gain.gain.setTargetAtTime(EPS, Math.max(t0 + 0.02, t1 - 0.08), 0.06);
  filter.connect(gain);
  gain.connect(dest);
  const v = makeVoice(gain, t1 + 0.12);
  const o = osc(g, 'square', freq);
  const o2 = osc(g, 'sine', freq);
  const o2g = g.ctx.createGain();
  o2g.gain.value = 0.6;
  o.connect(filter);
  o2.connect(o2g);
  o2g.connect(filter);
  o.start(t0);
  o2.start(t0);
  o.stop(t1 + 0.08);
  o2.stop(t1 + 0.08);
  v.sources.push(o, o2);
  return v;
}

/** Kick: pitch-swept sine 150 → 40 Hz with a snappy envelope. */
export function kickVoice(g: AudioGraph, dest: AudioNode, t0: number, level = 0.55): Voice {
  const gain = g.ctx.createGain();
  gain.gain.setValueAtTime(EPS, t0);
  gain.gain.linearRampToValueAtTime(level, t0 + 0.003);
  gain.gain.exponentialRampToValueAtTime(EPS, t0 + 0.22);
  gain.connect(dest);
  const o = osc(g, 'sine', 150);
  o.frequency.setValueAtTime(150, t0);
  o.frequency.exponentialRampToValueAtTime(40, t0 + 0.08);
  o.connect(gain);
  o.start(t0);
  o.stop(t0 + 0.25);
  const v = makeVoice(gain, t0 + 0.3);
  v.sources.push(o);
  return v;
}

/** Clap: three band-passed noise bursts 8 ms apart — reads as a clap, not a hiss. */
export function clapVoice(g: AudioGraph, dest: AudioNode, t0: number, level = 0.32): Voice {
  const gain = g.ctx.createGain();
  gain.gain.value = 1;
  gain.connect(dest);
  const v = makeVoice(gain, t0 + 0.15);
  for (let k = 0; k < 3; k++) {
    const t = t0 + k * 0.008;
    const src = noiseSource(g, false, t, 0.12);
    const bp = g.ctx.createBiquadFilter();
    bp.type = 'bandpass';
    bp.frequency.value = 1500;
    bp.Q.value = 1.2;
    const bg = g.ctx.createGain();
    bg.gain.setValueAtTime(EPS, t);
    bg.gain.linearRampToValueAtTime(level, t + 0.002);
    bg.gain.exponentialRampToValueAtTime(EPS, t + 0.09);
    src.connect(bp);
    bp.connect(bg);
    bg.connect(gain);
    v.sources.push(src);
  }
  return v;
}

/** Snare: clap body plus a tuned 190 Hz shell. */
export function snareVoice(g: AudioGraph, dest: AudioNode, t0: number, level = 0.3): Voice {
  const v = clapVoice(g, dest, t0, level * 0.8);
  const o = osc(g, 'triangle', 190);
  const og = g.ctx.createGain();
  envAD(og.gain, t0, level * 0.5, 1, 90);
  o.connect(og);
  og.connect(v.gain);
  o.start(t0);
  o.stop(t0 + 0.14);
  v.sources.push(o);
  return v;
}

/** Hat: highpassed noise, §12.4 R-MUSIC-L3 offbeats. */
export function hatVoice(g: AudioGraph, dest: AudioNode, t0: number, dur = 0.045, level = 0.14): Voice {
  const src = noiseSource(g, false, t0, dur + 0.02);
  const hp = g.ctx.createBiquadFilter();
  hp.type = 'highpass';
  hp.frequency.value = 6000;
  const gain = g.ctx.createGain();
  envAD(gain.gain, t0, level, 1, dur * 1000);
  src.connect(hp);
  hp.connect(gain);
  gain.connect(dest);
  const v = makeVoice(gain, t0 + dur + 0.03);
  v.sources.push(src);
  return v;
}

/** Noise sweep: band-passed noise whose center glides f0 → f1 (R-WHOOSH, R-RASP bodies). */
export function noiseSweepVoice(
  g: AudioGraph,
  dest: AudioNode,
  t0: number,
  f0: number,
  f1: number,
  durS: number,
  level: number,
  filterType: BiquadFilterType = 'bandpass',
  q = 2,
  brown = false,
): Voice {
  const src = noiseSource(g, brown, t0, durS + 0.05);
  const f = g.ctx.createBiquadFilter();
  f.type = filterType;
  f.Q.value = q;
  f.frequency.setValueAtTime(Math.max(20, f0), t0);
  f.frequency.exponentialRampToValueAtTime(Math.max(20, f1), t0 + durS);
  const gain = g.ctx.createGain();
  envAD(gain.gain, t0, level, 2, durS * 1000);
  src.connect(f);
  f.connect(gain);
  gain.connect(dest);
  const v = makeVoice(gain, t0 + durS + 0.06);
  v.sources.push(src);
  return v;
}

/** Pluck: triangle glide f0 → f1 plus a 3× partial (R-PLUCK). */
export function pluckVoice(
  g: AudioGraph,
  dest: AudioNode,
  t0: number,
  f0: number,
  f1: number,
  glideS: number,
  level: number,
  attackMs: number,
  decayMs: number,
  partialHz: number,
  partialGain: number,
): Voice {
  const gain = g.ctx.createGain();
  const end = envAD(gain.gain, t0, level, attackMs, decayMs);
  gain.connect(dest);
  const o = osc(g, 'triangle', f0);
  o.frequency.setValueAtTime(f0, t0);
  o.frequency.exponentialRampToValueAtTime(Math.max(20, f1), t0 + glideS);
  o.connect(gain);
  o.start(t0);
  o.stop(end + 0.02);
  const v = makeVoice(gain, end + 0.03);
  v.sources.push(o);
  if (partialGain > 0) {
    const p = osc(g, 'sine', partialHz);
    const pg = g.ctx.createGain();
    pg.gain.value = partialGain;
    p.connect(pg);
    pg.connect(gain);
    p.start(t0);
    p.stop(end + 0.02);
    v.sources.push(p);
  }
  return v;
}

/** Bell: fundamental plus inharmonic partials at 2.4× and 3.9× (R-GONG). */
export function bellVoice(
  g: AudioGraph,
  dest: AudioNode,
  t0: number,
  freq: number,
  level: number,
  decayMs: number,
): Voice {
  const gain = g.ctx.createGain();
  const end = envAD(gain.gain, t0, level, 2, decayMs);
  gain.connect(dest);
  const v = makeVoice(gain, end + 0.05);
  const ratios = PARTIAL_RATIOS;
  const gains = PARTIAL_GAINS;
  for (let i = 0; i < ratios.length; i++) {
    const o = osc(g, 'sine', freq * ratios[i]);
    const og = g.ctx.createGain();
    og.gain.value = gains[i];
    o.connect(og);
    og.connect(gain);
    o.start(t0);
    o.stop(end + 0.04);
    v.sources.push(o);
  }
  return v;
}

const PARTIAL_RATIOS = [1, 2.4, 3.9];
const PARTIAL_GAINS = [1, 0.4, 0.2];

/** Riser: sine up-gliss (R-CHIRP and the mine arm chirp). */
export function riserVoice(
  g: AudioGraph,
  dest: AudioNode,
  t0: number,
  f0: number,
  f1: number,
  durS: number,
  level: number,
  attackMs = 2,
  decayMs = 88,
  type: OscillatorType = 'sine',
): Voice {
  const gain = g.ctx.createGain();
  const end = envAD(gain.gain, t0, level, attackMs, decayMs);
  gain.connect(dest);
  const o = osc(g, type, f0);
  o.frequency.setValueAtTime(f0, t0);
  o.frequency.exponentialRampToValueAtTime(Math.max(20, f1), t0 + durS);
  o.connect(gain);
  o.start(t0);
  o.stop(Math.max(end, t0 + durS) + 0.02);
  const v = makeVoice(gain, Math.max(end, t0 + durS) + 0.03);
  v.sources.push(o);
  return v;
}

/** Sub-drop: sine falling f0 → f1 (R-NOVA body, R-MINE-BLAST, R-DEATH). */
export function subDropVoice(
  g: AudioGraph,
  dest: AudioNode,
  t0: number,
  f0: number,
  f1: number,
  durS: number,
  level: number,
  attackMs = 1,
  decayMs = 260,
): Voice {
  return riserVoice(g, dest, t0, f0, f1, durS, level, attackMs, decayMs, 'sine');
}

/** FM pair: carrier + modulator with an index that decays to 0 (R-CRIT, R-BOSS-ROAR). */
export function fmVoice(
  g: AudioGraph,
  dest: AudioNode,
  t0: number,
  carrierHz: number,
  modHz: number,
  index: number,
  durS: number,
  level: number,
  attackMs: number,
  decayMs: number,
): Voice {
  const gain = g.ctx.createGain();
  const end = envAD(gain.gain, t0, level, attackMs, decayMs);
  gain.connect(dest);
  const carrier = osc(g, 'sine', carrierHz);
  const mod = osc(g, 'sine', modHz);
  const modGain = g.ctx.createGain();
  modGain.gain.setValueAtTime(index * modHz, t0);
  modGain.gain.exponentialRampToValueAtTime(Math.max(1, modHz * 0.001), t0 + durS);
  mod.connect(modGain);
  modGain.connect(carrier.frequency);
  carrier.connect(gain);
  const stopAt = Math.max(end, t0 + durS) + 0.03;
  carrier.start(t0);
  mod.start(t0);
  carrier.stop(stopAt);
  mod.stop(stopAt);
  const v = makeVoice(gain, stopAt + 0.02);
  v.sources.push(carrier, mod);
  return v;
}

/** Simple tone with an AD envelope — the workhorse for R-TICK / R-UI-CLICK. */
export function toneVoice(
  g: AudioGraph,
  dest: AudioNode,
  t0: number,
  freq: number,
  type: OscillatorType,
  level: number,
  attackMs: number,
  decayMs: number,
): Voice {
  const gain = g.ctx.createGain();
  const end = envAD(gain.gain, t0, level, attackMs, decayMs);
  gain.connect(dest);
  const o = osc(g, type, freq);
  o.connect(gain);
  o.start(t0);
  o.stop(end + 0.02);
  const v = makeVoice(gain, end + 0.03);
  v.sources.push(o);
  return v;
}

/** Attaches an LFO to a param. Used for tremolo, chorus detune and phaser sweeps. */
export function attachLfo(
  g: AudioGraph,
  target: AudioParam,
  t0: number,
  rateHz: number,
  depth: number,
  type: OscillatorType = 'sine',
): OscillatorNode {
  const lfo = osc(g, type, rateHz);
  const lg = g.ctx.createGain();
  lg.gain.value = depth;
  lfo.connect(lg);
  lg.connect(target);
  lfo.start(t0);
  return lfo;
}

/** Convenience for loop bodies: raw noise routed through an arbitrary filter chain. */
export function loopNoise(g: AudioGraph, brown: boolean, t0: number): AudioBufferSourceNode {
  const s = g.ctx.createBufferSource();
  s.buffer = brown ? g.brown : g.noise;
  s.loop = true;
  s.start(t0);
  return s;
}

/** §12.2 rule (5): steals fade over 20 ms — never a hard cut. */
export function stopVoice(g: AudioGraph, v: Voice, at: number, fadeS = 0.02): void {
  const t = Math.max(at, g.ctx.currentTime);
  try {
    v.gain.gain.cancelScheduledValues(t);
    v.gain.gain.setValueAtTime(Math.max(EPS, v.gain.gain.value), t);
    v.gain.gain.linearRampToValueAtTime(0, t + fadeS);
  } catch {
    // Param already detached.
  }
  for (let i = 0; i < v.sources.length; i++) {
    try {
      v.sources[i].stop(t + fadeS + 0.005);
    } catch {
      // Source already stopped.
    }
  }
  v.endAt = t + fadeS + 0.01;
}

/** Appends every source of `src` to `dst` without a spread allocation. */
export function pushSources(dst: Voice, src: Voice): void {
  for (let i = 0; i < src.sources.length; i++) dst.sources.push(src.sources[i]);
  if (src.endAt > dst.endAt) dst.endAt = src.endAt;
}

export function disconnectVoice(v: Voice): void {
  try {
    v.gain.disconnect();
  } catch {
    // Already disconnected.
  }
  for (let i = 0; i < v.sources.length; i++) {
    try {
      v.sources[i].disconnect();
    } catch {
      // Already disconnected.
    }
  }
  v.sources.length = 0;
}
